import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import * as analytics from '@adsk/acs-web-analytics';
import { PRODUCT_IDS } from '@adsk/alloy-provider';
import { BasicButton } from '@adsk/alloy-react-button';
import Divider from '@adsk/alloy-react-divider';
import { OpenIcon, QuestionCircleFilledIcon } from '@adsk/alloy-react-icon';
import { useLocale } from '@adsk/alloy-react-locale';
import { MenuList, MenuItem } from '@adsk/alloy-react-menu';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import theme from '@adsk/alloy-react-theme';
import { Flyout } from '@adsk/alloy-react-tooltip';
import AboutBoxModal from './AboutBoxModal';
import { ANALYTICS_TYPES } from '../helpers/analytics';
import isURLValid from '../helpers/isURLValid';
import loadSupportScripts from '../helpers/loadSupportScripts';
import { licenseDataShape } from '../helpers/propTypes';
const defaultHelpLink = 'https://help.autodesk.com/view/BUILD/ENU/';
const QuestionMarkMenu = _ref => {
  let {
    initialOpen,
    helpLinks,
    licenseData,
    currentProductId,
    isProductRequired,
    hideContactSupport
  } = _ref;
  const {
    T
  } = useLocale(_ref2 => {
    let {
      locale
    } = _ref2;
    return require("../i18n/".concat(locale, ".json"));
  });
  const [menuOpen, setMenuOpen] = useState(!!initialOpen);
  const [aboutBoxOpen, setAboutBoxOpen] = useState(false);
  const [zvaLoaded, setZvaLoaded] = useState(window.SUPPORT_SCRIPTS_LOADED);
  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const toggleAboutBox = () => {
    setAboutBoxOpen(true);
    setMenuOpen(false);
    analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_HELP_LICENSEATTRIBUTIONS_CLICK);
  };
  const toggleSupport = () => {
    var _window;
    if ((_window = window) !== null && _window !== void 0 && _window.zoomCampaignSdk) {
      window.zoomCampaignSdk.show();
      window.zoomCampaignSdk.open();
    }
    setMenuOpen(false);
    analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_HELP_CONTACTSUPPORT_CLICK);
  };
  useEffect(() => {
    if (currentProductId || !isProductRequired) {
      loadSupportScripts(() => {
        if (mountedRef.current) {
          setZvaLoaded(true);
        }
      }, currentProductId);
    }
  }, [currentProductId, isProductRequired]);
  const closeChatWindow = useCallback(() => {
    var _window$zoomCampaignS, _window$zoomCampaignS2;
    setZvaLoaded(true);
    (_window$zoomCampaignS = window.zoomCampaignSdk) === null || _window$zoomCampaignS === void 0 ? void 0 : _window$zoomCampaignS.close();
    (_window$zoomCampaignS2 = window.zoomCampaignSdk) === null || _window$zoomCampaignS2 === void 0 ? void 0 : _window$zoomCampaignS2.hide();
  }, []);
  useEffect(() => {
    window.addEventListener('zoomCampaignSdk:ready', closeChatWindow);
    return () => {
      window.removeEventListener('zoomCampaignSdk:ready', closeChatWindow);
    };
  }, [closeChatWindow]);
  const helpLink = (helpLinks === null || helpLinks === void 0 ? void 0 : helpLinks.help) || currentProductId && isURLValid(T.translate("".concat(currentProductId.toUpperCase(), "_HELP_LINK"))) || defaultHelpLink;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(AboutBoxModal, {
    show: aboutBoxOpen,
    onClose: () => setAboutBoxOpen(false),
    licenseData: licenseData
  }), /*#__PURE__*/React.createElement(Flyout, {
    offset: 20,
    show: menuOpen,
    onHide: () => setMenuOpen(false),
    onShow: () => {
      setMenuOpen(true);
    },
    placement: Flyout.PLACEMENTS.BOTTOM,
    style: {
      padding: 0
    },
    content: /*#__PURE__*/React.createElement(MenuList, {
      style: {
        width: 300
      }
    }, /*#__PURE__*/React.createElement(MenuItem, {
      as: "a",
      href: helpLink,
      target: "_blank",
      rel: "noreferrer noopener",
      nested: false,
      showSelection: false,
      onClick: () => {
        setMenuOpen(false);
        analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_HELP_CLICK, {
          source_page: window.location.href
        });
      },
      style: {
        justifyContent: 'space-between',
        padding: '10px 16px'
      }
    }, /*#__PURE__*/React.createElement("span", null, T.translate('HELP')), /*#__PURE__*/React.createElement(OpenIcon, {
      size: 16,
      style: {
        marginLeft: 16
      }
    })), /*#__PURE__*/React.createElement(MenuItem, {
      as: "a",
      href: "https://learnacc.autodesk.com/",
      target: "_blank",
      rel: "noreferrer noopener",
      nested: false,
      showSelection: false,
      onClick: () => {
        setMenuOpen(false);
        analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_HELP_LEARNINGCENTER_CLICK);
      },
      style: {
        justifyContent: 'space-between',
        padding: '10px 16px'
      }
    }, /*#__PURE__*/React.createElement("span", null, T.translate('LEARNING_CENTER')), /*#__PURE__*/React.createElement(OpenIcon, {
      size: 16,
      style: {
        marginLeft: 16
      }
    })), !hideContactSupport && /*#__PURE__*/React.createElement(MenuItem, {
      nested: false,
      disabled: !zvaLoaded,
      showSelection: false,
      onClick: toggleSupport,
      style: {
        justifyContent: 'space-between',
        padding: '10px 16px'
      }
    }, /*#__PURE__*/React.createElement("span", null, T.translate('CONTACT_SUPPORT')), !zvaLoaded && /*#__PURE__*/React.createElement(ProgressRing, {
      size: ProgressRing.SIZES.XSMALL
    })), /*#__PURE__*/React.createElement(Divider, {
      style: {
        margin: '8px 0'
      }
    }), /*#__PURE__*/React.createElement(MenuItem, {
      as: "a",
      href: "https://www.autodesk.com/company/terms-of-use/en/general-terms",
      target: "_blank",
      rel: "noreferrer noopener",
      nested: false,
      showSelection: false,
      onClick: () => {
        setMenuOpen(false);
        analytics.track(ANALYTICS_TYPES.TOPNAVIGATION_HELP_TERMSCONDITIONS_CLICK);
      },
      style: {
        justifyContent: 'space-between',
        padding: '10px 16px'
      }
    }, /*#__PURE__*/React.createElement("span", null, T.translate('TERMS_AND_CONDITIONS')), /*#__PURE__*/React.createElement(OpenIcon, {
      size: 16
    })), !(helpLinks !== null && helpLinks !== void 0 && helpLinks.about) && /*#__PURE__*/React.createElement(MenuItem, {
      nested: false,
      showSelection: false,
      onClick: toggleAboutBox,
      style: {
        justifyContent: 'space-between',
        padding: '10px 16px'
      }
    }, /*#__PURE__*/React.createElement("span", null, T.translate('LICENSE_ATTRIBUTIONS'))), (helpLinks === null || helpLinks === void 0 ? void 0 : helpLinks.about) && /*#__PURE__*/React.createElement(MenuItem, {
      as: "a",
      href: helpLinks === null || helpLinks === void 0 ? void 0 : helpLinks.about,
      target: "_blank",
      rel: "noreferrer noopener",
      nested: false,
      showSelection: false,
      onClick: () => setMenuOpen(false),
      style: {
        justifyContent: 'space-between',
        padding: '10px 16px'
      }
    }, /*#__PURE__*/React.createElement("span", null, T.translate('ABOUT')), /*#__PURE__*/React.createElement(OpenIcon, {
      size: 16,
      style: {
        marginLeft: 16
      }
    })))
  }, /*#__PURE__*/React.createElement(BasicButton, {
    onClick: () => setMenuOpen(!menuOpen),
    "data-testid": "TopNavigation__help",
    style: {
      color: theme.colors.charcoal500,
      '&:hover': {
        color: theme.colors.charcoal700
      }
    }
  }, /*#__PURE__*/React.createElement(QuestionCircleFilledIcon, {
    size: 16
  }))));
};
QuestionMarkMenu.displayName = 'QuestionMarkMenu';
QuestionMarkMenu.propTypes = {
  /** Optionally toggle menu open on initial render */
  initialOpen: PropTypes.bool,
  /** Override default links */
  helpLinks: PropTypes.shape({
    help: PropTypes.string,
    about: PropTypes.string
  }),
  /** Use alternative license data (for demo purposes), this data is fetched by default */
  licenseData: licenseDataShape,
  /** current product id */
  currentProductId: PropTypes.oneOf(Object.values(PRODUCT_IDS)),
  /** is product required */
  isProductRequired: PropTypes.bool
};
export default QuestionMarkMenu;
//# sourceMappingURL=QuestionMarkMenu.js.map